import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Task } from '../../../../types/task';
import { TaskItem } from './TaskItem';
import DraggableTaskItem from './DraggableTaskItem';
import { TaskQuickAdd } from './TaskQuickAdd';
import { motion, AnimatePresence } from 'framer-motion';
import { AddIcon } from '../ios/IOSIcons';
import { useTranslation } from 'react-i18next';

interface TaskListProps {
  tasks: Task[];
  title: string;
  description?: string;
  projectId?: string;
  icon?: React.ReactNode;
  color?: string;
  onToggleTask: (taskId: string, completed: boolean) => void;
  onTaskClick: (taskId: string) => void;
  onEditProject?: (projectId: string) => void;
  onTitleClick?: () => void; // 新增：标题点击事件，用于导航到项目详情页
  hideProjectName?: boolean;
  id?: string; // 新增：用于滚动定位的ID
  noMargin?: boolean; // 新增：是否移除外边距，用于嵌套显示
  isTimeGroup?: boolean; // 新增：是否为时间组
  timeGroupDate?: Date | null; // 新增：时间组的日期
  showAddButton?: boolean; // 新增：是否显示添加按钮
  customTaskRenderer?: (task: Task, index: number, isFirst: boolean, isLast: boolean) => React.ReactNode; // 新增：自定义任务渲染器
  isCollapsed?: boolean; // 新增：是否折叠（外部控制）
  disableTitleClick?: boolean; // 新增：是否禁用标题点击，仅允许通过折叠箭头控制折叠状态
}

/**
 * iOS 风格的任务列表组件
 */
const TaskListComponent: React.FC<TaskListProps> = ({
  tasks,
  title,
  description,
  projectId,
  icon,
  color = 'text-ios-blue dark:text-ios-dark-blue',
  onToggleTask,
  onTaskClick,
  onEditProject,
  onTitleClick,
  hideProjectName = false,
  id,
  noMargin = false,
  isTimeGroup = false,
  timeGroupDate = null,
  showAddButton = true,
  customTaskRenderer,
  isCollapsed: externalIsCollapsed,
  disableTitleClick = false,
}) => {
  const { t } = useTranslation(['v3/tasks', 'common']);

  // 允许空项目显示
  // if (tasks.length === 0) return null;

  // 折叠状态 - 支持外部控制
  const [internalIsCollapsed, setInternalIsCollapsed] = useState(false);

  // 使用外部传入的折叠状态（如果提供），否则使用内部状态
  const isCollapsed = externalIsCollapsed !== undefined ? externalIsCollapsed : internalIsCollapsed;
  // 初始挂载状态，用于禁用初次动画
  const [isInitialMount, setIsInitialMount] = useState(true);
  // 快速添加状态
  const [showQuickAdd, setShowQuickAdd] = useState(false);

  useEffect(() => {
    // Set initial mount to false after the first render
    const timer = setTimeout(() => setIsInitialMount(false), 0); // Use timeout to ensure it runs after initial render cycle
    return () => clearTimeout(timer); // Cleanup timer
  }, []);

  // 长按状态
  const [isLongPressed, setIsLongPressed] = useState(false);
  // 长按计时器
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);

  // 切换折叠状态 - 使用 useCallback 优化
  const toggleCollapse = useCallback(() => {
    if (externalIsCollapsed !== undefined && onTitleClick) {
      // 如果提供了外部折叠状态和标题点击处理函数，则调用标题点击处理函数
      onTitleClick();
    } else {
      // 否则使用内部状态
      setInternalIsCollapsed(prev => !prev);
    }
  }, [externalIsCollapsed, onTitleClick]);

  // 处理长按开始 - 使用 useCallback 优化
  const handleLongPressStart = useCallback(() => {
    if (!projectId || !onEditProject) return;

    const timer = setTimeout(() => {
      setIsLongPressed(true);
      onEditProject(projectId);
    }, 500); // 500ms长按触发

    setLongPressTimer(timer);
  }, [projectId, onEditProject]);

  // 处理长按结束 - 使用 useCallback 优化
  const handleLongPressEnd = useCallback(() => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    setIsLongPressed(false);
  }, [longPressTimer]);

  // 处理标题点击 - 使用 useCallback 优化
  const handleTitleClick = useCallback(() => {
    // 如果禁用了标题点击，则不执行任何操作
    if (disableTitleClick) return;

    if (!isLongPressed) {
      if (onTitleClick) {
        // 如果提供了标题点击处理函数，则调用它
        onTitleClick();
      } else {
        // 否则使用默认的折叠行为
        toggleCollapse();
      }
    }
  }, [disableTitleClick, isLongPressed, onTitleClick, toggleCollapse]);

  // 处理折叠箭头点击 - 使用 useCallback 优化
  const handleCollapseClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止事件冒泡
    if (!isLongPressed) {
      toggleCollapse();
    }
  }, [isLongPressed, toggleCollapse]);

  // 处理添加按钮点击 - 使用 useCallback 优化
  const handleAddButtonClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止事件冒泡
    setShowQuickAdd(prev => !prev);
  }, []);

  // 处理快速添加关闭 - 使用 useCallback 优化
  const handleQuickAddClose = useCallback(() => {
    setShowQuickAdd(false);
  }, []);

  // 使用 useMemo 优化空状态文本
  const emptyStateText = useMemo(() => {
    if (title === t('v3/tasks:categories.incomplete', '未完成')) return t('v3/tasks:emptyState.noIncompleteTasks', '暂无未完成任务');
    if (title === t('v3/tasks:categories.completed', '已完成')) return t('v3/tasks:emptyState.noCompletedTasks', '暂无已完成任务');
    return t('v3/tasks:emptyState.noTasks', '暂无任务');
  }, [title, t]);

  return (
    <div id={id} className={`${noMargin ? '' : 'mb-4'} animate-ios-fade-in`}>
      {/* 列表标题 - 标题区域和折叠箭头分开点击 */}
      <div className={`${noMargin ? 'px-0' : 'px-4'} mb-2.5`}>
        <div
          className={`flex items-center ${color} transition-opacity py-0.5 w-full overflow-hidden`}
          aria-expanded={!isCollapsed}
          role="button"
        >
          {/* 标题和图标 - 可点击进入详情或折叠 */}
          <div
            className={`flex items-center flex-1 min-w-0 overflow-hidden pr-2 ${disableTitleClick ? '' : 'cursor-pointer active:opacity-70'}`}
            onClick={handleTitleClick}
            onMouseDown={disableTitleClick ? undefined : handleLongPressStart}
            onMouseUp={disableTitleClick ? undefined : handleLongPressEnd}
            onMouseLeave={disableTitleClick ? undefined : handleLongPressEnd}
            onTouchStart={disableTitleClick ? undefined : handleLongPressStart}
            onTouchEnd={disableTitleClick ? undefined : handleLongPressEnd}
            onTouchCancel={disableTitleClick ? undefined : handleLongPressEnd}
          >
            {icon && (
              <div className={`mr-2 flex-shrink-0 ${title === t('v3/tasks:categories.noProject') || projectId ? 'bg-white dark:bg-ios-dark-gray5 rounded-full w-8 h-8 flex items-center justify-center' : 'w-5 h-5'}`}>
                <div className={title === t('v3/tasks:categories.noProject') || projectId ? 'w-5 h-5' : 'w-full h-full'}>
                  {icon}
                </div>
              </div>
            )}
            <div className="flex flex-col justify-center w-full min-w-0 overflow-hidden">
              <div className="flex items-center w-full overflow-hidden">
                <h2 className="text-ios-headline font-semibold truncate flex-1 mr-2">{title}</h2>
                <div className="bg-white/90 dark:bg-ios-dark-gray5/90 px-1.5 rounded-full text-ios-footnote flex-shrink-0 min-w-[1.5rem] text-center shadow-sm">
                  {tasks.length}
                </div>
              </div>

              {/* 描述显示在标题下方 */}
              {description && (
                <div className="text-ios-footnote text-ios-gray2 dark:text-ios-dark-gray2 truncate w-full">
                  {description}
                </div>
              )}
            </div>
          </div>

          {/* 右侧操作区 - 添加按钮和折叠箭头，单独可点击 */}
          <div className="flex items-center">
            {/* 添加按钮 - 带旋转动画 */}
            {showAddButton && (
              <div
                className="flex items-center p-2 cursor-pointer active:opacity-70 rounded-full hover:bg-ios-gray6/80 dark:hover:bg-ios-dark-gray4/80"
                onClick={handleAddButtonClick}
                aria-label={t('v3/tasks:actions.quickAdd')}
              >
                <div
                  className={`w-4 h-4 transition-transform duration-300 ease-in-out ${showQuickAdd ? 'rotate-45 text-red-500 dark:text-red-400' : 'text-ios-gray3 dark:text-ios-dark-gray3'}`}
                >
                  <AddIcon />
                </div>
              </div>
            )}

            {/* 折叠指示箭头 */}
            <div
              className="flex items-center ml-1 p-2 cursor-pointer active:opacity-70 rounded-full hover:bg-ios-gray6/80 dark:hover:bg-ios-dark-gray4/80"
              onClick={handleCollapseClick}
            >
              <div className={`w-4 h-4 transition-transform ${isCollapsed ? '' : 'rotate-90'} text-ios-gray3 dark:text-ios-dark-gray3`}>
                <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 快速添加表单 */}
      <AnimatePresence>
        {showQuickAdd && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden mb-2"
          >
            <div className={`${noMargin ? 'mx-0' : 'mx-4'}`}>
              <TaskQuickAdd
                projectId={projectId}
                isTimeGroup={isTimeGroup}
                timeGroupDate={timeGroupDate}
                onClose={handleQuickAddClose}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 任务列表 - iOS风格，白色背景，圆角，可折叠，带动画 */}
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            // Conditionally set transition duration
            transition={ isInitialMount
              ? { duration: 0 } // No animation on initial mount
              : { duration: 0.2, ease: 'easeInOut' } // Animation on toggle
            }
            className="overflow-hidden"
          >
            <div className={`${noMargin ? 'mx-0' : 'mx-4'} bg-white dark:bg-ios-dark-gray5 rounded-ios-lg overflow-hidden shadow-ios dark:shadow-ios-dark`}>
              {tasks.length > 0 ? (
                tasks.map((task, index) => (
                  customTaskRenderer ? (
                    <React.Fragment key={task.id}>
                      {customTaskRenderer(
                        task,
                        index,
                        index === 0,
                        index === tasks.length - 1
                      )}
                    </React.Fragment>
                  ) : (
                    <DraggableTaskItem
                      key={task.id}
                      task={task}
                      onToggle={onToggleTask}
                      onClick={onTaskClick}
                      onProjectClick={projectId && onTitleClick ? () => onTitleClick() : undefined}
                      isFirst={index === 0}
                      isLast={index === tasks.length - 1}
                      hideProjectName={hideProjectName}
                    />
                  )
                ))
              ) : (
                // 空项目状态
                <div className="py-4 px-5 text-center text-ios-gray dark:text-ios-dark-gray text-ios-body">
                  {emptyStateText}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// 使用 React.memo 包装组件，避免不必要的重新渲染
export const TaskList = React.memo(TaskListComponent, (prevProps, nextProps) => {
  // 自定义比较函数，只有在关键属性变化时才重新渲染

  // 比较任务数组是否相同 - 只比较长度和第一个/最后一个任务的ID
  const tasksEqual =
    prevProps.tasks.length === nextProps.tasks.length &&
    (prevProps.tasks.length === 0 ||
      (prevProps.tasks[0]?.id === nextProps.tasks[0]?.id &&
       prevProps.tasks[prevProps.tasks.length - 1]?.id === nextProps.tasks[nextProps.tasks.length - 1]?.id));

  return (
    tasksEqual &&
    prevProps.title === nextProps.title &&
    prevProps.description === nextProps.description &&
    prevProps.projectId === nextProps.projectId &&
    prevProps.color === nextProps.color &&
    prevProps.hideProjectName === nextProps.hideProjectName &&
    prevProps.noMargin === nextProps.noMargin &&
    prevProps.isTimeGroup === nextProps.isTimeGroup &&
    prevProps.showAddButton === nextProps.showAddButton &&
    prevProps.isCollapsed === nextProps.isCollapsed &&
    prevProps.disableTitleClick === nextProps.disableTitleClick
  );
});
